import React, { useState, useEffect, useCallback } from 'react';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from 'lib/supabase';
import { format, subDays, startOfMonth, parseISO } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { X, Info } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────
const SUPER_ADMIN_EMAIL = 'pederzollisimone@gmail.com';
const ADMIN_EMAIL = 'salem.hlila@icloud.com';
const PAGE_SIZE = 25;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatTimestamp = (ts) => {
  if (!ts) return '-';
  try { return format(parseISO(ts), 'dd/MM/yyyy HH:mm:ss'); } catch { return ts; }
};

const formatActiveTime = (minutes) => {
  if (!minutes || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// Format Italian relative time: "oggi alle HH:mm", "ieri alle HH:mm", "N giorni fa"
const formatItalianRelative = (ts) => {
  if (!ts) return '—';
  try {
    const date = typeof ts === 'string' ? parseISO(ts) : new Date(ts);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    const timeStr = format(date, 'HH:mm');
    if (diffDays === 0) return `oggi alle ${timeStr}`;
    if (diffDays === 1) return `ieri alle ${timeStr}`;
    return `${diffDays} giorni fa`;
  } catch { return '—'; }
};

// ─── JSON Viewer Modal ────────────────────────────────────────────────────────
const JsonModal = ({ data, onClose }) => {
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Dettagli</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-auto p-5 flex-1">
          <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-4 whitespace-pre-wrap break-all">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

// ─── Productivity Card ────────────────────────────────────────────────────────
const ProductivityCard = ({ label, email, actionsThisMonth, presenceMinutes, lastSignIn, lastAction, color }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);

  return (
    <div className={`bg-white rounded-xl border-2 p-6 flex-1 min-w-0`} style={{ borderColor: color }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
      </div>
      <p className="text-sm text-gray-500 truncate mb-4">{email}</p>
      <div className="space-y-3">
        <div>
          <p className="text-4xl font-bold text-gray-900">{actionsThisMonth ?? '—'}</p>
          <p className="text-sm text-gray-500 mt-0.5">Azioni questo mese</p>
        </div>
        <div className="border-t border-gray-100 pt-3 space-y-2">
          {/* Tempo presente (heartbeat-based) with tooltip */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1 relative">
              <span className="text-sm text-gray-500">Tempo presente</span>
              <button
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                onMouseEnter={() => setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}
                onClick={() => setTooltipVisible(v => !v)}
                aria-label="Informazioni"
              >
                <Info size={14} />
              </button>
              {tooltipVisible && (
                <div className="absolute bottom-full left-0 mb-2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl z-10 leading-relaxed">
                  Stima basata sul tracciamento di presenza ogni 60 secondi mentre la pagina è attiva.
                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" />
                </div>
              )}
            </div>
            <span className="text-sm font-semibold text-gray-800">{formatActiveTime(presenceMinutes)}</span>
          </div>
          {/* Ultimo accesso */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Ultimo accesso</span>
            <span className="text-sm font-semibold text-gray-800">{formatItalianRelative(lastSignIn)}</span>
          </div>
          {/* Ultima azione */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Ultima azione</span>
            <span className="text-sm font-semibold text-gray-800">{formatItalianRelative(lastAction)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminAuditLog = () => {
  // ── All logs (for aggregations) ──
  const [allLogs, setAllLogs] = useState([]);
  const [adminUsers, setAdminUsers] = useState({});
  const [loadingAgg, setLoadingAgg] = useState(true);

  // ── Productivity stats ──
  const [superAdminStats, setSuperAdminStats] = useState({ actionsThisMonth: 0, presenceMinutes: 0, lastSignIn: null, lastAction: null });
  const [adminStats, setAdminStats] = useState({ actionsThisMonth: 0, presenceMinutes: 0, lastSignIn: null, lastAction: null });

  // ── Daily chart data (last 30 days) ──
  const [dailyChartData, setDailyChartData] = useState([]);

  // ── Action types chart (this month) ──
  const [actionTypesData, setActionTypesData] = useState([]);

  // ── Filterable log ──
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingLogs, setLoadingLogs] = useState(true);

  // ── Filters ──
  const [filterAdmin, setFilterAdmin] = useState('all'); // 'all' | 'superadmin' | 'admin'
  const [filterAction, setFilterAction] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [distinctActions, setDistinctActions] = useState([]);

  // ── JSON modal ──
  const [jsonModal, setJsonModal] = useState(null);

  // ── Fetch all logs for aggregations ──
  const fetchAllLogs = useCallback(async () => {
    setLoadingAgg(true);
    try {
      // Fetch audit logs and RPC productivity metrics in parallel
      const [logsResult, rpcResult] = await Promise.all([
        supabase?.from('audit_logs')?.select(`*, admin:users!audit_logs_admin_id_fkey(email, role, full_name)`)?.order('created_at', { ascending: false }),
        supabase?.rpc('get_admin_productivity_metrics')
      ]);

      if (logsResult?.error) throw logsResult?.error;

      const logsData = logsResult?.data || [];
      setAllLogs(logsData);

      // Build admin users map by email
      const usersMap = {};
      logsData?.forEach(log => {
        if (log?.admin?.email) {
          usersMap[log.admin.email] = log?.admin;
        }
      });
      setAdminUsers(usersMap);

      // Distinct actions
      const actions = [...new Set(logsData.map(l => l.action).filter(Boolean))]?.sort();
      setDistinctActions(actions);

      // ── Productivity stats from RPC ──
      const rpcByEmail = {};
      if (!rpcResult?.error && rpcResult?.data) {
        rpcResult?.data?.forEach(row => {
          if (row?.email) rpcByEmail[row.email] = row;
        });
      }

      const buildStats = (email) => {
        const row = rpcByEmail?.[email];
        return {
          actionsThisMonth: row?.actions_this_month ?? 0,
          presenceMinutes: row?.heartbeats_this_month ?? 0, // each heartbeat = 1 minute
          lastSignIn: row?.last_sign_in_at ?? null,
          lastAction: row?.last_action_at ?? null,
        };
      };

      setSuperAdminStats(buildStats(SUPER_ADMIN_EMAIL));
      setAdminStats(buildStats(ADMIN_EMAIL));

      // ── Daily chart data (last 30 days) — unchanged, uses audit_logs ──
      const today = new Date();
      const days = [];
      for (let i = 29; i >= 0; i--) {
        const d = subDays(today, i);
        const key = format(d, 'yyyy-MM-dd');
        days?.push({ date: key, label: format(d, 'dd/MM'), superAdmin: 0, admin: 0 });
      }
      const dayMap = {};
      days?.forEach(d => { dayMap[d.date] = d; });

      logsData?.forEach(log => {
        if (!log?.created_at) return;
        const key = format(new Date(log.created_at), 'yyyy-MM-dd');
        if (!dayMap?.[key]) return;
        if (log?.admin?.email === SUPER_ADMIN_EMAIL) dayMap[key].superAdmin++;
        else if (log?.admin?.email === ADMIN_EMAIL) dayMap[key].admin++;
      });
      setDailyChartData(days);

      // ── Action types chart (this month) — unchanged, uses audit_logs ──
      const now = new Date();
      const monthStart = startOfMonth(now);
      const thisMonthLogs = logsData?.filter(l => l?.created_at && new Date(l.created_at) >= monthStart);
      const actionMap = {};
      thisMonthLogs?.forEach(log => {
        if (!log?.action) return;
        if (!actionMap?.[log?.action]) actionMap[log.action] = { action: log?.action, superAdmin: 0, admin: 0 };
        if (log?.admin?.email === SUPER_ADMIN_EMAIL) actionMap[log.action].superAdmin++;
        else if (log?.admin?.email === ADMIN_EMAIL) actionMap[log.action].admin++;
      });
      const actionTypes = Object.values(actionMap)?.map(a => ({ ...a, total: a?.superAdmin + a?.admin }))?.sort((a, b) => b?.total - a?.total)?.slice(0, 10);
      setActionTypesData(actionTypes);

    } catch (err) {
      console.error('Error fetching aggregation data:', err);
    } finally {
      setLoadingAgg(false);
    }
  }, []);

  // ── Fetch paginated logs with filters ──
  const fetchLogs = useCallback(async (page) => {
    setLoadingLogs(true);
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase?.from('audit_logs')?.select(`*, admin:users!audit_logs_admin_id_fkey(email, role, full_name)`, { count: 'exact' })?.order('created_at', { ascending: false })?.range(from, to);

      // Filter by admin
      if (filterAdmin === 'superadmin') {
        const saLog = allLogs?.find(l => l?.admin?.email === SUPER_ADMIN_EMAIL);
        if (saLog?.admin_id) query = query?.eq('admin_id', saLog?.admin_id);
      } else if (filterAdmin === 'admin') {
        const aLog = allLogs?.find(l => l?.admin?.email === ADMIN_EMAIL);
        if (aLog?.admin_id) query = query?.eq('admin_id', aLog?.admin_id);
      }

      if (filterAction) query = query?.eq('action', filterAction);
      if (filterDateFrom) query = query?.gte('created_at', filterDateFrom + 'T00:00:00');
      if (filterDateTo) query = query?.lte('created_at', filterDateTo + 'T23:59:59');

      const { data, count, error } = await query;
      if (error) throw error;

      setLogs(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  }, [filterAdmin, filterAction, filterDateFrom, filterDateTo, allLogs]);

  useEffect(() => { fetchAllLogs(); }, [fetchAllLogs]);

  useEffect(() => {
    if (!loadingAgg) fetchLogs(currentPage);
  }, [currentPage, loadingAgg, fetchLogs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterAdmin, filterAction, filterDateFrom, filterDateTo]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // ── Custom tooltip for daily chart ──
  const DailyTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-semibold text-gray-700 mb-1">{label}</p>
        {payload?.map(p => (
          <p key={p?.name} style={{ color: p?.fill }}>
            {p?.name}: <strong>{p?.value}</strong>
          </p>
        ))}
      </div>
    );
  };

  return (
    <AdminDashboardLayout>
      <div className="space-y-8">

        {/* ── Header ── */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit & Produttività</h1>
          <p className="text-gray-500 mt-1">Attività di amministratori e super amministratori</p>
        </div>

        {/* ── Section 1: Productivity Cards ── */}
        {loadingAgg ? (
          <div className="flex gap-6">
            {[0, 1]?.map(i => (
              <div key={i} className="flex-1 bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-48" />
            ))}
          </div>
        ) : (
          <div className="flex gap-6 flex-wrap">
            <ProductivityCard
              label="Super Admin"
              email={SUPER_ADMIN_EMAIL}
              actionsThisMonth={superAdminStats?.actionsThisMonth}
              presenceMinutes={superAdminStats?.presenceMinutes}
              lastSignIn={superAdminStats?.lastSignIn}
              lastAction={superAdminStats?.lastAction}
              color="#104d98"
            />
            <ProductivityCard
              label="Admin"
              email={ADMIN_EMAIL}
              actionsThisMonth={adminStats?.actionsThisMonth}
              presenceMinutes={adminStats?.presenceMinutes}
              lastSignIn={adminStats?.lastSignIn}
              lastAction={adminStats?.lastAction}
              color="#32b3e5"
            />
          </div>
        )}

        {/* ── Section 2: Daily Actions Bar Chart ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5">Azioni per giorno (ultimi 30 giorni)</h2>
          {loadingAgg ? (
            <div className="h-64 animate-pulse bg-gray-100 rounded-lg" />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dailyChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  interval={4}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<DailyTooltip />} />
                <Legend
                  formatter={(value) => <span style={{ fontSize: 12, color: '#374151' }}>{value}</span>}
                />
                <Bar dataKey="superAdmin" name="Super Admin" stackId="a" fill="#104d98" radius={[0, 0, 0, 0]} />
                <Bar dataKey="admin" name="Admin" stackId="a" fill="#32b3e5" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ── Section 3: Action Types Horizontal Bar Chart ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Tipi di azione (questo mese)</h2>
          <p className="text-sm text-gray-400 mb-5">Top 10 azioni per frequenza</p>
          {loadingAgg ? (
            <div className="h-64 animate-pulse bg-gray-100 rounded-lg" />
          ) : actionTypesData?.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">Nessuna azione questo mese</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(240, actionTypesData?.length * 36)}>
              <BarChart
                data={actionTypesData}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="action"
                  tick={{ fontSize: 11, fill: '#374151' }}
                  tickLine={false}
                  width={220}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: 12 }}
                />
                <Legend
                  formatter={(value) => <span style={{ fontSize: 12, color: '#374151' }}>{value}</span>}
                />
                <Bar dataKey="superAdmin" name="Super Admin" stackId="a" fill="#104d98" />
                <Bar dataKey="admin" name="Admin" stackId="a" fill="#32b3e5" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ── Section 4: Filterable Action Log ── */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Log azioni</h2>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              {/* Admin filter */}
              <select
                value={filterAdmin}
                onChange={(e) => setFilterAdmin(e?.target?.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tutti gli admin</option>
                <option value="superadmin">Super Admin</option>
                <option value="admin">Admin</option>
              </select>

              {/* Action filter */}
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e?.target?.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tutte le azioni</option>
                {distinctActions?.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>

              {/* Date from */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500 whitespace-nowrap">Dal</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e?.target?.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Date to */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500 whitespace-nowrap">Al</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e?.target?.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Clear filters */}
              {(filterAdmin !== 'all' || filterAction || filterDateFrom || filterDateTo) && (
                <button
                  onClick={() => { setFilterAdmin('all'); setFilterAction(''); setFilterDateFrom(''); setFilterDateTo(''); }}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Azzera filtri
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  {['Timestamp', 'Admin', 'Azione', 'Tipo entità', 'ID entità', 'Dettagli']?.map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loadingLogs ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <div className="flex items-center justify-center gap-3 text-gray-400">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">Caricamento...</span>
                      </div>
                    </td>
                  </tr>
                ) : logs?.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-400">
                      Nessun risultato trovato
                    </td>
                  </tr>
                ) : (
                  logs?.map(log => {
                    const isSuperAdmin = log?.admin?.email === SUPER_ADMIN_EMAIL;
                    const isAdmin = log?.admin?.email === ADMIN_EMAIL;
                    const hasDetails = log?.old_value || log?.new_value;
                    return (
                      <tr key={log?.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-700">
                          {formatTimestamp(log?.created_at)}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            {isSuperAdmin && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#104d98' }}>
                                Super Admin
                              </span>
                            )}
                            {isAdmin && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#32b3e5' }}>
                                Admin
                              </span>
                            )}
                            <span className="text-gray-600 text-xs">{log?.admin?.email || '-'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm">
                          <code className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono">
                            {log?.action || '-'}
                          </code>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-600">
                          {log?.entity_type || '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-400 font-mono" title={log?.entity_id || ''}>
                          {log?.entity_id ? log?.entity_id?.substring(0, 8) + '…' : '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm">
                          {hasDetails ? (
                            <button
                              onClick={() => setJsonModal({ old_value: log?.old_value, new_value: log?.new_value })}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                            >
                              Visualizza
                            </button>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loadingLogs && totalPages > 1 && (
            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} di {totalCount} risultati
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ← Precedente
                </button>
                <span className="text-sm text-gray-500">
                  Pag. {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Successiva →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* JSON Modal */}
      {jsonModal && (
        <JsonModal data={jsonModal} onClose={() => setJsonModal(null)} />
      )}
    </AdminDashboardLayout>
  );
};

export default AdminAuditLog;